import React, {
	useCallback,
	useState,
	useContext,
	useMemo,
	useRef,
	useEffect,
} from "react";
import { ElementComponentProps } from "elements/types";
import { useSelector } from "react-redux";
import {
	getLegacyArrayViewData,
	getIds,
	getLabelsAndIdsForElements,
	prepSearchConfig,
	getElementIdByName,
} from "../../data/form/elementSelectors";
import { State } from "data";
import Element from "components/Forms/Element";
import { useDispatch } from "react-redux";
import { jsonPatch } from "../../data/form/formActions";
import EditForm from "./EditForm";
import {
	IconButton,
	Dropdown,
	DropdownMenuButton,
	Elem,
	ElemBefore,
	Button,
} from "@alm/flow-ds/components";
import { LegacyArrayViewElement } from "records/Element";
import { useTranslation } from "react-i18next";
import { LayoutContext } from "../../contexts/LayoutContext";
import { Column } from "react-table";
import Finder, { AddSelectedCallback } from "components/Finder/Finder";
import { Checkbox } from "@alm/flow-ds/components";
import DeleteDialog from "components/DeleteDialog";
import { addRow, removeRow, RowData } from "./utils";
import Table from "./Table";
import { setLegacyCheckboxes } from "data/app/appActions";
import { getLegacyCheckboxes } from "data/app/appSelectors";
import LegacyDropdown from "./LegacyDropdown";

function LegacyArrayView({
	traversal,
	id,
	index,
	uuid,
	fid,
	vid,
	label,
	rowIndex,
	hint,
	disabled,
	required,
	limit,
	search,
	searchField,
}: ElementComponentProps<LegacyArrayViewElement>) {
	const { t } = useTranslation();

	const dispatch = useDispatch();
	const layout = useContext(LayoutContext);
	const [finderOpen, setFinderOpen] = useState(false);
	const [activeRow, setActiveRow] = useState<number | null>(null);
	const [isApplyClicked, setIsApplyClicked] = useState<boolean>(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
	const [fieldsIsEditedStatus, setFieldsIsEditedStatus] = useState<{
		[key: string]: boolean;
	}>({});
	const [rowsLength, setRowsLength] = useState<number>(0);

	// Keeps track of where to return focus when EditForm is closed
	const activeRef = useRef<HTMLButtonElement | null>(null);

	const selectorProps = useMemo(() => {
		return { uuid, fid, vid, index, rowIndex, id };
	}, [uuid, fid, vid, index, rowIndex, id]);

	// Get an ID using
	const searchFieldId = useSelector((state: State) => {
		if (searchField) return getElementIdByName(state, fid, searchField);
	});

	// Returns [[label, id], ...]
	const traversalWithLabelsAndIds = useSelector((state: State) =>
		getLabelsAndIdsForElements(state, fid, traversal)
	);

	// Returns [[uuid, id], ...]
	const traversalWithIds = useSelector((state: State) =>
		getIds(state, fid, traversal)
	);

	// Creates rows from our columned data
	const data = useSelector((state: State) =>
		getLegacyArrayViewData(state, fid, vid, uuid)
	);

	const searchConfig = useSelector((state: State) => {
		if (search) return prepSearchConfig(state, selectorProps, search);
	});

	const legacyCheckboxes = useSelector(getLegacyCheckboxes);

	const checkboxes = useMemo(() => {
		return legacyCheckboxes[uuid] || [];
	}, [legacyCheckboxes, uuid]);

	// Add a row
	const add = useCallback(() => {
		dispatch(
			jsonPatch(
				fid,
				addRow({ traversal: traversalWithIds, vid, rowIndex, data })
			)
		);
		setActiveRow(data.length);
		setRowsLength(prevLength => prevLength + 1);
		layout.openSidebar?.();
	}, [data, dispatch, fid, layout, rowIndex, traversalWithIds, vid]);

	const closeEditor = useCallback(() => {
		layout.closeSidebar?.();
		// Return focus to the correct edit icon after sidebar is closed
		window.setTimeout(() => {
			activeRef.current && activeRef.current.focus();
			setActiveRow(null);
			setIsApplyClicked(false);
		}, 0);
	}, [layout]);

	const toggleFinder = useCallback(() => {
		setFinderOpen(!finderOpen);
	}, [finderOpen]);

	const selectedCount = useMemo(() => checkboxes.length, [checkboxes]);

	const handleSearchAdd: AddSelectedCallback = useCallback(
		selected => {
			const { values } = selected;
			if (searchFieldId) {
				const rows = values.reduce((prev: RowData, current) => {
					const row = {
						[searchFieldId]: { value: current, id: searchFieldId },
					};

					return [...prev, row];
				}, []);

				dispatch(
					jsonPatch(
						fid,
						addRow({
							traversal: traversalWithIds,
							vid,
							rowIndex,
							data,
							rowData: rows,
						})
					)
				);
			}
		},
		[data, dispatch, fid, rowIndex, searchFieldId, traversalWithIds, vid]
	);

	// Remove a row
	const remove = useCallback(
		(index: number) => {
			// Close the sidebar if we are deleting the active row
			if (activeRow === index) {
				closeEditor();
			}

			// Update the active row if a row with a lower index is removed
			if (activeRow && activeRow > index) setActiveRow(activeRow - 1);

			dispatch(
				jsonPatch(
					fid,
					removeRow(index, traversalWithIds, vid, rowIndex)
				)
			);
			setRowsLength(prevLength => prevLength - 1);
		},
		[activeRow, traversalWithIds, dispatch, fid, closeEditor, rowIndex, vid]
	);

	const removeSelectedRows = useCallback(() => {
		let offset = 0;
		const sortedCheckboxes = [...checkboxes].sort();
		for (const index of sortedCheckboxes) {
			remove(index - offset++);
		}
		dispatch(setLegacyCheckboxes(uuid, []));
	}, [checkboxes, dispatch, remove, uuid]);

	const removeElements = useCallback(() => {
		if (activeRow !== undefined) {
			selectedCount && checkboxes.includes(activeRow ?? -1)
				? removeSelectedRows()
				: remove(activeRow ?? -1);
		} else if (selectedCount) {
			removeSelectedRows();
		}
	}, [checkboxes, remove, removeSelectedRows, activeRow, selectedCount]);

	const handleRemove = () => setIsDeleteModalOpen(true);

	const editRow = useCallback(
		(index?: number) => {
			index === undefined
				? setActiveRow(checkboxes[0])
				: setActiveRow(index);
			layout.openSidebar?.();
		},
		[checkboxes, layout]
	);

	// Setting up the table data by using results array
	const tableData = useMemo(() => {
		return data?.map((_, dataIndex) => {
			const props = traversalWithIds?.map(id => {
				return {
					[id[1]]: (
						<Element
							displayOnly={true}
							fid={fid}
							uuid={id[0]}
							index={dataIndex}
							rowIndex={rowIndex}
						/>
					),
				};
			});
			return { ...Object.assign({}, ...props) };
		});
	}, [data, fid, rowIndex, traversalWithIds]);

	const handleSelectAllCheckboxesClick = useCallback(() => {
		const newCheckboxes: number[] = [];
		const isEveryTrue = selectedCount === rowsLength;
		if (!isEveryTrue) {
			for (let i = 0; i < rowsLength; i++) {
				newCheckboxes.push(i);
			}
		}
		dispatch(setLegacyCheckboxes(uuid, newCheckboxes));
	}, [dispatch, rowsLength, selectedCount, uuid]);

	// Setting up the column names
	const tableColumns = useMemo(() => {
		const columns: Array<
			Column<{
				[key: string]: string;
			}> & { sticky?: string }
		> = traversalWithLabelsAndIds?.map(column => {
			return {
				Header: column[0] || "",
				accessor: column[1] || "",
				width: 150,
			};
		});

		if (disabled !== true) {
			columns.unshift({
				Header: (
					<Checkbox
						id={`selectAllCheckboxes_${uuid}`}
						checked={Boolean(selectedCount === tableData.length)}
						value={
							Boolean(
								selectedCount === tableData.length
							) as unknown as string
						}
						onChange={handleSelectAllCheckboxesClick}
						label=""
						ariaLabel={`Select all checkboxes ${uuid}`}
					/>
				),
				id: "checkboxes",
				accessor: "checkboxes",
				width: 50,
				minWidth: 50,
				disableResizing: true,
				Cell: ({ row }: { row: { index: number } }) => {
					const index = row.index;
					return (
						<div className="legacyArrayView-tr-checkbox">
							<Checkbox
								id={`${index}_${uuid}`}
								checked={checkboxes.includes(index)}
								value={
									checkboxes.includes(
										index
									) as unknown as string
								}
								// eslint-disable-next-line @typescript-eslint/no-empty-function
								onChange={() => {}}
								label=""
								ariaLabel={`Select checkbox at index ${index}`}
							/>
						</div>
					);
				},
			});

			// Add column for Edit and Delete actions
			columns.push({
				Header: selectedCount ? (
					<div className="legacyArrayView-actions">
						<Dropdown
							id={`${uuid}_menu_selectAll`}
							align="right"
							disabled={selectedCount === 0}
							className="selectAll_menu_dropdown"
							menuBtn={
								<IconButton
									icon="ellipsis-v"
									label={t("main.labels.menu")}
									className="btn-basic--default btn--round"
								/>
							}>
							<DropdownMenuButton
								label={
									checkboxes.length
										? `${t("common.labels.edit")} (${
												checkboxes.length
										  })`
										: t("common.labels.edit")
								}
								onClick={() => editRow()}
							/>
							<DropdownMenuButton
								label={
									checkboxes.length
										? `${t("common.labels.delete")} (${
												checkboxes.length
										  })`
										: t("common.labels.delete")
								}
								onClick={() => {
									handleRemove();
									setActiveRow(checkboxes[0]);
								}}
							/>
						</Dropdown>
					</div>
				) : (
					""
				),
				id: "actions",
				accessor: "actions",
				width: 50,
				minWidth: 50,
				disableResizing: true,
				sticky: "right",
				Cell: ({ row }: { row: { index: number } }) => {
					return (
						<LegacyDropdown
							uuid={uuid}
							row={row}
							disabled={disabled}
							editRow={editRow}
							setActiveRow={setActiveRow}
							handleRemove={handleRemove}
						/>
					);
				},
			});
		}
		return columns;
	}, [
		traversalWithLabelsAndIds,
		uuid,
		selectedCount,
		tableData.length,
		handleSelectAllCheckboxesClick,
		t,
		checkboxes,
		editRow,
		disabled,
	]);

	const handleOnApplyClick = useCallback(() => setIsApplyClicked(true), []);

	useEffect(() => {
		const newFieldsIsEditedStatus: { [key: string]: boolean } = {};
		for (const uuid of traversal) {
			newFieldsIsEditedStatus[uuid] = false;
		}
		setFieldsIsEditedStatus(newFieldsIsEditedStatus);
	}, [isApplyClicked, traversal]);

	useEffect(() => {
		if (isApplyClicked) closeEditor();
	}, [closeEditor, isApplyClicked]);

	const deleteDialogText = useMemo(
		() =>
			`${t("elements.legacyArrayView.deleteTextPart1")} ${
				checkboxes.includes(activeRow ?? -1) ? checkboxes.length : 1
			} ${t("elements.legacyArrayView.deleteTextPart2")}`,
		[activeRow, checkboxes, t]
	);

	return (
		<Elem className="elem-legacyArrayView legacyArrayView">
			<div className="legacyArrayView-metadata">
				<ElemBefore label={label} required={required} />
				<ElemBefore
					label={
						selectedCount
							? `${selectedCount} ${t(
									"elements.legacyArrayView.rowsSelected"
							  )}`
							: ""
					}
					required={required}
				/>
			</div>
			{hint && (
				<div className="elem__hint elem__hint--legacyArrayView">
					{hint}
				</div>
			)}

			{data.length ? (
				<Table
					disabled={disabled}
					legacyId={uuid}
					tableData={tableData}
					tableColumns={tableColumns}
					setRowsLength={setRowsLength}
				/>
			) : null}

			{!disabled && (
				<>
					<button
						disabled={
							limit === undefined
								? false
								: traversalWithIds.length < limit
								? false
								: true
						}
						onClick={add}
						className="btn btn-tertiary legacyArrayView__add-btn mt-2">
						{t("main.labels.add")}
					</button>
					{searchConfig && (
						<button
							disabled={
								limit === undefined
									? false
									: traversalWithIds.length < limit
									? false
									: true
							}
							onClick={toggleFinder}
							className="btn btn-tertiary legacyArrayView__search-add-btn mt-2">
							{t("elements.finder.searchAndAdd")}
						</button>
					)}
				</>
			)}

			{activeRow !== null && (
				<EditForm
					title={
						selectedCount &&
						checkboxes.includes(activeRow) &&
						activeRow < data.length
							? `${selectedCount} rows`
							: label
					}
					close={closeEditor}
					loadingContent={false}>
					{traversal.map(uuid => (
						<Element
							removeable={false}
							index={activeRow}
							indexes={
								selectedCount &&
								checkboxes.includes(activeRow) &&
								activeRow < data.length
									? checkboxes
									: undefined
							}
							fid={fid}
							uuid={uuid}
							rowIndex={rowIndex}
							key={`${uuid}`}
							isApplyClicked={isApplyClicked}
							fieldsIsEditedStatus={fieldsIsEditedStatus}
							setFieldsIsEditedStatus={setFieldsIsEditedStatus}
						/>
					))}
					<Button
						label={t("common.labels.apply")}
						name="Apply"
						className="btn-primary"
						onClick={handleOnApplyClick}
					/>
				</EditForm>
			)}

			{searchConfig && finderOpen && (
				<Finder
					{...searchConfig}
					onCloseClick={toggleFinder}
					onAddSelected={handleSearchAdd}
					title={label}
					selectLimit={limit}
				/>
			)}
			{
				<DeleteDialog
					isOpen={isDeleteModalOpen}
					setIsOpen={setIsDeleteModalOpen}
					title="Delete"
					content={deleteDialogText}
					deleteElement={removeElements}
				/>
			}
		</Elem>
	);
}

export default React.memo(LegacyArrayView);
